/**
 * DDA System Data Service - ENHANCED WITH SEMANTIC SEARCH
 * Provides access to live DDA project data, contractor performance, and budget tracking
 */

import SemanticSearchService from './semanticSearchService';

class DDADataService {
  static projectEmbeddings = new Map(); // Cache for DDA project embeddings
  static OPENAI_API_KEY = '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';
  
  static getApiBase() {
    return process.env.VUE_APP_ENV_API_BASE || "http://167.172.25.255:3000/api/v1/";
  }

  /**
   * Get authentication headers (same way as other components)
   */
  static getHeaders() {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  }

  /**
   * Get all projects from DDA system
   */
  static async getProjects() {
    try {
      const apiBase = this.getApiBase();
      const response = await fetch(`${apiBase}projects`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        console.warn('Live project system unavailable:', response.status);
        return [];
      }
      
      return await response.json();
    } catch (error) {
      console.warn('Live project system unavailable:', error.message);
      return [];
    }
  }

  /**
   * Get project details by ID
   */
  static async getProject(projectId) {
    try {
      const apiBase = this.getApiBase();
      const response = await fetch(`${apiBase}projects/${projectId}`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        return null;
      }
      
      return await response.json();
    } catch (error) {
      console.warn('Live project detail unavailable:', error.message);
      return null;
    }
  }

  /**
   * Get draws for a specific project
   */
  static async getProjectDraws(projectId) {
    try {
      const apiBase = this.getApiBase();
      const response = await fetch(`${apiBase}draws/for_project/${projectId}`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        return [];
      }
      
      return await response.json();
    } catch (error) {
      console.warn('Live system draws unavailable:', error.message);
      return [];
    }
  }

  /**
   * Get draw details (budget line items) for analysis
   */
  static async getDrawDetails(filters = {}) {
    try {
      const apiBase = this.getApiBase();
      const queryParams = new URLSearchParams(filters);
      const response = await fetch(`${apiBase}draw_details?${queryParams}`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        return [];
      }
      
      return await response.json();
    } catch (error) {
      console.warn('Live system draw details unavailable:', error.message);
      return [];
    }
  }

  /**
   * Get contractors data for performance analysis - uses G702/G703 performance endpoint
   */
  static async getContractors() {
    try {
      const apiBase = this.getApiBase();
      
      // First get all project IDs to fetch contractor performance across all projects
      const projects = await this.getProjects();
      const projectIds = projects.map(p => p.id).slice(0, 50).join(','); // Limit to 50 projects
      
      if (!projectIds) {
        console.warn('No projects available for contractor performance');
        return [];
      }
      
      // Use the contractor performance endpoint which has actual G702/G703 data
      const response = await fetch(`${apiBase}projects/fetch_contractor_performance?project_ids=${projectIds}`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        // Fallback to basic contractors endpoint
        const fallbackResponse = await fetch(`${apiBase}contractors`, {
          headers: this.getHeaders()
        });
        return fallbackResponse.ok ? await fallbackResponse.json() : [];
      }
      
      const data = await response.json();
      console.log('📊 Fetched contractor performance data:', data.contractors?.length || 0, 'contractors');
      
      // Transform to include performance metrics
      return (data.contractors || []).map(c => {
        const originalContract = parseFloat(c.original_contract_sum || 0);
        const currentContract = parseFloat(c.contract_sum_to_date || 0);
        const totalCompleted = parseFloat(c.total_completed_and_stored_to_date || 0);
        const budgetVariance = currentContract - originalContract;
        
        return {
          id: c.contractor_id,
          name: c.contractor_name,
          company_name: c.contractor_name,
          trade: c.dda_term || c.dda_category || c.scope_detail || 'General',
          scope_of_work: c.scope_detail || c.dda_term || c.dda_category || 'General Work',
          project_id: c.project_id,
          project_name: c.project_name || `Project ${c.project_id}`,  // 🔥 Use API project_name
          // Performance metrics from G702/G703
          original_contract: originalContract,
          current_contract: currentContract,
          total_completed: totalCompleted,
          budget_variance: budgetVariance,
          variance_percent: originalContract > 0 
            ? ((budgetVariance / originalContract) * 100).toFixed(1) + '%'
            : '0%',
          percent_complete: c.percent_complete || (currentContract > 0 
            ? ((totalCompleted / currentContract) * 100).toFixed(1) 
            : 0),
          draw_number: c.draw_number,
          application_number: c.application_number,
          source: 'G702/G703 Performance Data'
        };
      });
    } catch (error) {
      console.warn('Live system contractors unavailable:', error.message);
      return [];
    }
  }

  /**
   * Get contractors for a specific project
   */
  static async getProjectContractors(projectId) {
    try {
      const apiBase = this.getApiBase();
      const response = await fetch(`${apiBase}contractors/project_contractors/${projectId}`, {
        headers: this.getHeaders()
      });
      
      if (!response.ok) {
        return [];
      }
      
      return await response.json();
    } catch (error) {
      console.warn('Live system project contractors unavailable:', error.message);
      return [];
    }
  }

  /**
   * INTELLIGENT search for DDA projects - SEMANTIC SEARCH + SMART FILTERING + AI
   */
  static async searchData(query) {
    if (!query) {
      try {
        const [projects, contractors, drawDetails] = await Promise.all([
          this.getProjects(),
          this.getContractors(),
          this.getDrawDetails()
        ]);

        return {
          projects: projects.slice(0, 50),
          contractors: contractors.slice(0, 25),
          drawDetails: drawDetails.slice(0, 100),
          suggestions: [],
          totalCount: projects.length
        };
      } catch (error) {
        console.warn('DDA data unavailable:', error.message);
        return { projects: [], contractors: [], drawDetails: [], suggestions: [], totalCount: 0 };
      }
    }

    console.log('🔍 DDA SEMANTIC SEARCH: Processing query:', query);
    
    // STEP 1: Expand query with construction ontology
    const expansion = SemanticSearchService.expandQueryTerms(query);
    console.log('🧠 DDA query expanded with', expansion.expandedTerms.length, 'terms');
    
    // STEP 2: Generate embedding for query
    const queryEmbedding = await SemanticSearchService.getEmbedding(query, this.OPENAI_API_KEY);

    // STEP 3: SMART QUERY PARSING - Extract location, building type, contractors, etc.
    const queryFilters = this.parseQuery(query);
    console.log('DDA parsed query filters:', queryFilters);

    try {
      // Get all available data in parallel
      const [allProjects, allContractors, allDrawDetails] = await Promise.all([
        this.getProjects(),
        this.getContractors(),
        this.getDrawDetails()
      ]);

      // INTELLIGENT PROJECT FILTERING
      let filteredProjects = allProjects.filter(project => {
        let matches = true;

        // Location filtering with intelligent matching
        if (queryFilters.locations.length > 0) {
          const projectLocation = this.normalizeLocation(project);
          matches = matches && queryFilters.locations.some(loc => 
            this.locationMatches(projectLocation, loc)
          );
        }

        // Building type filtering (DDA uses different fields than SPD)
        if (queryFilters.buildingTypes.length > 0) {
          const projectType = this.normalizeBuildingType(project);
          console.log(`🔍 Checking project "${project.name}" with types:`, projectType.buildingUseTypes, 'against query:', queryFilters.buildingTypes);
          
          const typeMatches = queryFilters.buildingTypes.some(type => 
            this.buildingTypeMatches(projectType, type)
          );
          
          if (typeMatches) {
            console.log(`✅ MATCHED: "${project.name}" matched query types:`, queryFilters.buildingTypes);
          } else {
            console.log(`❌ FILTERED OUT: "${project.name}" did NOT match query types:`, queryFilters.buildingTypes);
          }
          
          matches = matches && typeMatches;
        }

        // Budget range filtering
        if (queryFilters.budgetRange.min || queryFilters.budgetRange.max) {
          const budget = project.total_original_budget || project.current_budget || 0;
          if (budget > 0) {
            if (queryFilters.budgetRange.min && budget < queryFilters.budgetRange.min) matches = false;
            if (queryFilters.budgetRange.max && budget > queryFilters.budgetRange.max) matches = false;
          }
        }

        // Square footage range filtering
        if (queryFilters.squareFootageRange && (queryFilters.squareFootageRange.min || queryFilters.squareFootageRange.max)) {
          const squareFootage = project.total_gsf || project.initial_gsf || 0;
          if (squareFootage > 0) {
            if (queryFilters.squareFootageRange.min && squareFootage < queryFilters.squareFootageRange.min) matches = false;
            if (queryFilters.squareFootageRange.max && squareFootage > queryFilters.squareFootageRange.max) matches = false;
          } else {
            // If project has no square footage data, exclude it from size-based searches
            if (queryFilters.squareFootageRange.min || queryFilters.squareFootageRange.max) {
              matches = false;
            }
          }
        }

        // Units range filtering
        if (queryFilters.unitsRange && (queryFilters.unitsRange.min || queryFilters.unitsRange.max)) {
          const units = project.total_num_units || 0;
          if (units > 0) {
            if (queryFilters.unitsRange.min && units < queryFilters.unitsRange.min) matches = false;
            if (queryFilters.unitsRange.max && units > queryFilters.unitsRange.max) matches = false;
          } else {
            // If project has no units data, exclude it from units-based searches
            if (queryFilters.unitsRange.min || queryFilters.unitsRange.max) {
              matches = false;
            }
          }
        }

        // Floors range filtering
        if (queryFilters.floorsRange && (queryFilters.floorsRange.min || queryFilters.floorsRange.max)) {
          const floors = project.num_floors || 0;
          if (floors > 0) {
            if (queryFilters.floorsRange.min && floors < queryFilters.floorsRange.min) matches = false;
            if (queryFilters.floorsRange.max && floors > queryFilters.floorsRange.max) matches = false;
          } else {
            // If project has no floors data, exclude it from floors-based searches
            if (queryFilters.floorsRange.min || queryFilters.floorsRange.max) {
              matches = false;
            }
          }
        }

        // Parking type filtering (this would need to check against project parking data)
        if (queryFilters.parkingTypes && queryFilters.parkingTypes.length > 0) {
          // TODO: Implement parking type matching when DDA has parking data fields
          // For now, this is a placeholder for future enhancement
        }

        // Construction type filtering
        if (queryFilters.constructionTypes && queryFilters.constructionTypes.length > 0) {
          const projectConstructionTypes = project.project_construction_types_s || project.project_construction_types_abv || [];
          if (projectConstructionTypes.length > 0) {
            // Check if any of the project's construction types match the search criteria
            const hasMatchingConstructionType = queryFilters.constructionTypes.some(searchType => 
              projectConstructionTypes.some(projectType => 
                projectType && projectType.toLowerCase().includes(searchType.toLowerCase())
              )
            );
            matches = matches && hasMatchingConstructionType;
          } else {
            // If project has no construction type data, exclude it from construction type searches
            matches = false;
          }
        }

        // Year filtering for project dates
        if (queryFilters.years.length > 0) {
          const projectYear = this.extractProjectYear(project);
          if (projectYear) {
            matches = matches && queryFilters.years.includes(projectYear);
          }
        }

        // Contractor filtering
        if (queryFilters.contractors.length > 0) {
          // This would require cross-referencing with draw details or project contractors
          // For now, we'll handle this in the AI analysis phase
        }

        return matches;
      });

      // STEP 4: Apply concept-based filters from ontology (height class, etc.)
      filteredProjects = SemanticSearchService.applyConceptFilters(filteredProjects, expansion.concepts);
      
      console.log(`DDA filtered from ${allProjects.length} to ${filteredProjects.length} projects`);
      
      // 🔥 CRITICAL FIX: Filter contractors to ONLY those from the FINAL filtered projects
      // This ensures contractors match the exact projects returned (Manhattan condos, etc.)
      const finalProjectIds = new Set(filteredProjects.map(p => p.id));
      
      // First filter by project - only include contractors from matching projects
      let filteredContractors = allContractors.filter(contractor => {
        return finalProjectIds.has(contractor.project_id);
      });
      
      console.log(`📊 DDA contractor search: ${filteredContractors.length} contractors from ${finalProjectIds.size} matching projects`);
      
      // Additionally filter by contractor name if specified in query
      if (queryFilters.contractors.length > 0) {
        filteredContractors = filteredContractors.filter(contractor => {
          const contractorName = (contractor.name || contractor.company_name || '').toLowerCase();
          return queryFilters.contractors.some(name => 
            contractorName.includes(name.toLowerCase())
          );
        });
        console.log(`📊 After name filter: ${filteredContractors.length} contractors`);
      }

      // STEP 5: Calculate semantic similarity scores if embedding available
      let scoredProjects = filteredProjects;
      if (queryEmbedding && filteredProjects.length > 0) {
        console.log('🎯 Calculating DDA semantic similarity scores...');
        
        // Generate or retrieve project embeddings
        const projectsWithScores = await Promise.all(
          filteredProjects.map(async (project) => {
            const projectText = SemanticSearchService.createProjectSearchText(project);
            const projectId = project.id;
            
            // Get or generate project embedding
            let projectEmbedding = this.projectEmbeddings.get(projectId);
            if (!projectEmbedding) {
              projectEmbedding = await SemanticSearchService.getEmbedding(projectText, this.OPENAI_API_KEY);
              if (projectEmbedding) {
                this.projectEmbeddings.set(projectId, projectEmbedding);
              }
            }
            
            // Calculate similarity
            const similarity = projectEmbedding 
              ? SemanticSearchService.cosineSimilarity(queryEmbedding, projectEmbedding)
              : 0;
            
            return {
              ...project,
              semanticScore: similarity,
              projectText: projectText
            };
          })
        );
        
        // Sort by semantic similarity (highest first)
        scoredProjects = projectsWithScores.sort((a, b) => b.semanticScore - a.semanticScore);
        console.log('🎯 Top DDA match semantic score:', scoredProjects[0]?.semanticScore);
      }

      // STEP 6: Enrich filtered projects for AI analysis
      const enrichedProjects = scoredProjects.map(project => {
        const costPerSF = project.total_gsf > 0 ? (project.total_original_budget || 0) / project.total_gsf : 0;
        
        return {
          ...project,
          id: project.id,
          name: project.name,
          entityName: project.entity_name,
          address: project.address_line_1,
          city: project.city,
          state: project.state,
          zipcode: project.zipcode,
          status: project.status,
          totalGSF: project.total_gsf,
          numFloors: project.num_floors,
          totalHeight: project.total_height,
          totalUnits: project.total_num_units,
          originalBudget: project.total_original_budget,
          costPerSF: costPerSF,
          
          // Enhanced cost data for DDA projects
          costs: {
            originalBudget: project.total_original_budget,
            currentBudget: project.current_budget,
            totalPaid: project.total_paid,
            remaining: project.remaining_budget,
            costPerSF: costPerSF,
            costPerUnit: project.total_num_units > 0 ? (project.total_original_budget || 0) / project.total_num_units : 0
          },

          dates: {
            plannedStart: project.planned_start_date,
            plannedEnd: project.planned_end_date,
            actualStart: project.actual_start_date,
            actualEnd: project.actual_end_date
          },

          searchableText: [
            project.name,
            project.entity_name,
            project.address_line_1,
            project.city,
            project.state,
            project.notes
          ].filter(Boolean).join(' '),

          source: 'DDA System',
          details: project
        };
      });

      // Enrich contractors
      const enrichedContractors = filteredContractors.map(contractor => ({
        ...contractor,
        name: contractor.name,
        companyName: contractor.company_name,
        trade: contractor.trade,
        location: contractor.location,
        source: 'DDA System',
        details: contractor
      }));

      // Filter draw details based on projects and contractors
      const projectIds = new Set(enrichedProjects.map(p => p.id));
      const contractorNames = new Set(enrichedContractors.map(c => c.name || c.companyName));
      
      let filteredDrawDetails = allDrawDetails.filter(detail => {
        // Include if related to filtered projects or contractors
        return projectIds.has(detail.project_id) || 
               contractorNames.has(detail.company) ||
               contractorNames.has(detail.contractor_name);
      });
      
      // IMPORTANT: If project filter returned 0 but we have contractors, still include them
      // This ensures contractor queries work even if location filter is too strict
      if (enrichedProjects.length === 0 && enrichedContractors.length > 0) {
        console.log('⚠️ No projects matched location filter, but returning', enrichedContractors.length, 'contractors anyway');
      }

      const enrichedDrawDetails = filteredDrawDetails.map(detail => ({
        id: detail.id,
        description: detail.description,
        company: detail.company,
        tradeCode: detail.trade_code,
        originalBudget: detail.original_budget,
        currentBudget: detail.current_budget,
        billedToDate: detail.billed_to_date,
        remainingBudget: detail.remaining_budget,
        dateSubmitted: detail.date_submitted,
        periodThrough: detail.period_through,
        notes: detail.notes,
        projectId: detail.project_id,
        source: 'DDA System',
        details: detail
      }));

      return {
        projects: enrichedProjects.slice(0, 50), // Send most relevant subset to AI
        contractors: enrichedContractors.slice(0, 25),
        drawDetails: enrichedDrawDetails.slice(0, 100),
        suggestions: [],
        totalCount: enrichedProjects.length,
        queryFilters: queryFilters, // Pass filters to AI for context
        rawProjects: enrichedProjects // All filtered results
      };

    } catch (error) {
      console.warn('DDA search failed:', error.message);
      return { projects: [], contractors: [], drawDetails: [], suggestions: [], totalCount: 0 };
    }
  }

  /**
   * SMART QUERY PARSING - Extract location, building types, contractors, budget ranges
   */
  static parseQuery(query) {
    const queryLower = query.toLowerCase();
    
    // Location extraction with common aliases (same as SPD)
    const locations = [];
    const locationPatterns = {
      'manhattan': ['manhattan', 'nyc', 'new york city', 'new york, ny'],
      'brooklyn': ['brooklyn', 'bk'],
      'miami': ['miami', 'miami beach', 'south beach'],
      'chicago': ['chicago', 'windy city'],
      'los angeles': ['los angeles', 'la', 'hollywood'],
      'san francisco': ['san francisco', 'sf', 'bay area'],
      'boston': ['boston', 'beantown'],
      'seattle': ['seattle'],
      'denver': ['denver'],
      'austin': ['austin'],
      'atlanta': ['atlanta'],
      'washington': ['dc', 'washington dc', 'washington, dc'],
      'philadelphia': ['philadelphia', 'philly'],
      'dallas': ['dallas'],
      'houston': ['houston']
    };
    
    for (const [city, aliases] of Object.entries(locationPatterns)) {
      if (aliases.some(alias => queryLower.includes(alias))) {
        locations.push(city);
      }
    }
    
    // Building type extraction with DDA-specific categories
    const buildingTypes = [];
    const typePatterns = {
      'residential': ['condo', 'condominium', 'apartment', 'residential', 'multifamily', 'housing', 'tower', 'high-rise', 'luxury'],
      'office': ['office', 'commercial', 'corporate', 'workplace', 'business'],
      'retail': ['retail', 'shopping', 'store', 'mall', 'plaza'],
      'hotel': ['hotel', 'hospitality', 'resort', 'inn'],
      'mixed-use': ['mixed-use', 'mixed use', 'mixed'],
      'industrial': ['industrial', 'warehouse', 'manufacturing'],
      'healthcare': ['hospital', 'medical', 'healthcare', 'clinic'],
      'education': ['school', 'university', 'college', 'education'],
      'cultural': ['museum', 'theater', 'cultural', 'art']
    };
    
    for (const [type, keywords] of Object.entries(typePatterns)) {
      if (keywords.some(keyword => queryLower.includes(keyword))) {
        buildingTypes.push(type);
      }
    }
    
    // Budget range extraction ($ amounts)
    const budgetRange = { min: null, max: null };
    const budgetMatches = query.match(/\$(\d+(?:,\d{3})*(?:\.\d{2})?)/g);
    if (budgetMatches && budgetMatches.length >= 1) {
      const budgets = budgetMatches.map(m => parseInt(m.replace(/[$,]/g, '')));
      if (budgets.length === 1) {
        budgetRange.max = budgets[0];
      } else {
        budgetRange.min = Math.min(...budgets);
        budgetRange.max = Math.max(...budgets);
      }
    }
    
    // Square footage range extraction (sf/sqft amounts)
    const squareFootageRange = { min: null, max: null };
    
    // Extract "over X sf" and "under Y sf" patterns
    const overSfMatch = query.match(/over\s+(\d+(?:,\d{3})*)\s+(?:sf|sqft|square\s*feet?)/i);
    const underSfMatch = query.match(/under\s+(\d+(?:,\d{3})*)\s+(?:sf|sqft|square\s*feet?)/i);
    
    if (overSfMatch) {
      squareFootageRange.min = parseInt(overSfMatch[1].replace(/,/g, ''));
    }
    if (underSfMatch) {
      squareFootageRange.max = parseInt(underSfMatch[1].replace(/,/g, ''));
    }
    
    // Also try general square footage number extraction as fallback
    if (!squareFootageRange.min && !squareFootageRange.max) {
      const sfMatches = query.match(/(\d+(?:,\d{3})*)\s*(?:sf|sqft|square\s*feet?)/gi);
      if (sfMatches && sfMatches.length >= 1) {
        const sizes = sfMatches.map(m => parseInt(m.replace(/[sf,sqft,square,feet,\s]/gi, '').replace(/,/g, '')));
        if (sizes.length === 1) {
          squareFootageRange.max = sizes[0];
        } else {
          squareFootageRange.min = Math.min(...sizes);
          squareFootageRange.max = Math.max(...sizes);
        }
      }
    }
    
    // Number of units range extraction
    const unitsRange = { min: null, max: null };
    const overUnitsMatch = query.match(/over\s+(\d+(?:,\d{3})*)\s+(?:units?|apartments?|condos?)/i);
    const underUnitsMatch = query.match(/under\s+(\d+(?:,\d{3})*)\s+(?:units?|apartments?|condos?)/i);
    
    if (overUnitsMatch) {
      unitsRange.min = parseInt(overUnitsMatch[1].replace(/,/g, ''));
    }
    if (underUnitsMatch) {
      unitsRange.max = parseInt(underUnitsMatch[1].replace(/,/g, ''));
    }
    
    // Number of floors range extraction
    const floorsRange = { min: null, max: null };
    const overFloorsMatch = query.match(/over\s+(\d+(?:,\d{3})*)\s+(?:floors?|stories?|story)/i);
    const underFloorsMatch = query.match(/under\s+(\d+(?:,\d{3})*)\s+(?:floors?|stories?|story)/i);
    const highRiseMatch = query.match(/high-?rise|skyscraper|tower/i);
    const lowRiseMatch = query.match(/low-?rise|townhouse|garden/i);
    
    if (overFloorsMatch) {
      floorsRange.min = parseInt(overFloorsMatch[1].replace(/,/g, ''));
    }
    if (underFloorsMatch) {
      floorsRange.max = parseInt(underFloorsMatch[1].replace(/,/g, ''));
    }
    if (highRiseMatch) {
      floorsRange.min = 10; // High-rise typically 10+ floors
    }
    if (lowRiseMatch) {
      floorsRange.max = 4; // Low-rise typically 4 or fewer floors
    }
    
    // Parking type extraction
    const parkingTypes = [];
    const parkingPatterns = {
      'surface': ['surface parking', 'surface lot', 'parking lot'],
      'structured': ['structured parking', 'parking garage', 'parking structure'],
      'underground': ['underground parking', 'below-grade parking', 'basement parking'],
      'automated': ['automated parking', 'robotic parking'],
      'valet': ['valet parking'],
      'offsite': ['offsite parking', 'off-site parking']
    };
    
    for (const [type, keywords] of Object.entries(parkingPatterns)) {
      if (keywords.some(keyword => queryLower.includes(keyword))) {
        parkingTypes.push(type);
      }
    }
    
    // Construction type extraction (using exact DDA construction type names)
    const constructionTypes = [];
    const constructionPatterns = {
      'Ground-Up': ['ground up', 'ground-up', 'new construction', 'new build', 'ground up construction'],
      'Renovation': ['renovation', 'renovate', 'rehab', 'restoration', 'remodel', 'renovating'],
      'Adaptive Reuse': ['adaptive reuse', 'adaptive-reuse', 'conversion', 'repurpose', 'adaptive'],
      'Extension / Overbuild': ['extension', 'overbuild', 'expansion', 'add-on', 'addition'],
      'Renovation / Extension': ['renovation and extension', 'renovation/extension', 'renovation extension', 'renovate and extend']
    };
    
    for (const [type, keywords] of Object.entries(constructionPatterns)) {
      if (keywords.some(keyword => queryLower.includes(keyword))) {
        constructionTypes.push(type);
      }
    }

    // Year extraction for date filtering
    const years = [];
    const yearMatches = query.match(/\b(20\d{2})\b/g);
    if (yearMatches) {
      years.push(...yearMatches.map(y => parseInt(y)));
    }

    // Contractor/company extraction (common construction company names)
    const contractors = [];
    const contractorPatterns = [
      'turner', 'skanska', 'clark', 'gilbane', 'suffolk', 'whiting', 'lendlease',
      'tishman', 'plaza', 'hunter roberts', 'structure tone', 'mcnamara', 'yorke'
    ];
    
    for (const contractor of contractorPatterns) {
      if (queryLower.includes(contractor)) {
        contractors.push(contractor);
      }
    }
    
    return {
      locations,
      buildingTypes,
      budgetRange,
      squareFootageRange,
      unitsRange,
      floorsRange,
      parkingTypes,
      constructionTypes,
      years,
      contractors,
      originalQuery: query
    };
  }

  /**
   * LOCATION NORMALIZATION AND MATCHING (same as SPD)
   */
  /**
   * Determine NYC borough from zip code
   */
  static getBoroughFromZip(zipcode) {
    if (!zipcode) return null;
    const zip = parseInt(zipcode.toString().substring(0, 5));
    
    // Manhattan zip codes: 10001-10282
    if (zip >= 10001 && zip <= 10282) return 'manhattan';
    // Brooklyn zip codes: 11201-11256
    if (zip >= 11201 && zip <= 11256) return 'brooklyn';
    // Queens zip codes: 11004-11109, 11351-11697
    if ((zip >= 11004 && zip <= 11109) || (zip >= 11351 && zip <= 11697)) return 'queens';
    // Bronx zip codes: 10451-10475
    if (zip >= 10451 && zip <= 10475) return 'bronx';
    // Staten Island zip codes: 10301-10314
    if (zip >= 10301 && zip <= 10314) return 'staten island';
    
    return null;
  }

  static normalizeLocation(project) {
    const city = (project.city || '').toLowerCase().trim();
    const state = (project.state || '').toLowerCase().trim();
    const address = (project.address_line_1 || '').toLowerCase().trim();
    const zipcode = project.zipcode || project.zip || '';
    
    // Determine NYC borough from zip code
    const borough = this.getBoroughFromZip(zipcode);
    
    return {
      city,
      state,
      address,
      zipcode,
      borough, // Will be 'manhattan', 'brooklyn', etc. for NYC projects
      fullLocation: `${city}, ${state}`.trim()
    };
  }
  
  static locationMatches(projectLocation, queryLocation) {
    const queryLower = queryLocation.toLowerCase().trim();
    const cityLower = projectLocation.city.toLowerCase();
    const stateLower = projectLocation.state.toLowerCase();
    const addressLower = projectLocation.address.toLowerCase();
    const borough = projectLocation.borough; // From zip code
    
    // Direct city match
    if (cityLower.includes(queryLower)) return true;
    
    // NYC Borough mapping - use zip codes to determine actual borough
    const nycBoroughs = ['manhattan', 'brooklyn', 'queens', 'bronx', 'staten island'];
    const nycCities = ['new york', 'new york city', 'nyc', 'ny'];
    const nycAliases = {
      'manhattan': ['manhattan', 'midtown', 'downtown', 'uptown', 'harlem', 'tribeca', 'soho', 'chelsea', 'financial district'],
      'brooklyn': ['brooklyn', 'bk', 'williamsburg', 'dumbo', 'bushwick', 'bed-stuy', 'park slope'],
      'queens': ['queens', 'astoria', 'long island city', 'lic', 'flushing', 'jamaica'],
      'bronx': ['bronx', 'the bronx'],
      'staten island': ['staten island', 'si']
    };
    
    // Check if query is asking for a specific borough
    for (const [boroughName, aliases] of Object.entries(nycAliases)) {
      if (aliases.some(alias => queryLower.includes(alias))) {
        // User is searching for this borough
        // Match if project's ZIP-determined borough matches
        if (borough === boroughName) {
          console.log(`📍 ZIP match: ${projectLocation.zipcode} → ${borough} matches query "${queryLower}"`);
          return true;
        }
        // Also match if city explicitly contains the borough name
        if (cityLower.includes(boroughName)) return true;
      }
    }
    
    // If searching for NYC/New York generally, match ALL NYC projects (any borough)
    if (nycCities.some(nyc => queryLower.includes(nyc)) || queryLower === 'nyc') {
      if (borough) {
        // Has a NYC borough from zip code
        return true;
      }
      if (cityLower.includes('new york') || 
          nycBoroughs.some(b => cityLower.includes(b)) ||
          stateLower === 'ny') {
        return true;
      }
    }
    
    // State abbreviation handling
    const stateMap = {
      'ny': ['new york', 'nyc', 'manhattan', 'brooklyn', 'queens', 'bronx'],
      'nj': ['new jersey', 'jersey city', 'hoboken', 'newark'],
      'ca': ['california', 'los angeles', 'san francisco', 'la', 'sf', 'san diego'],
      'fl': ['florida', 'miami', 'orlando', 'tampa'],
      'tx': ['texas', 'houston', 'dallas', 'austin', 'san antonio'],
      'il': ['illinois', 'chicago'],
      'wa': ['washington', 'seattle'],
      'ma': ['massachusetts', 'boston'],
      'dc': ['washington dc', 'dc', 'district of columbia']
    };
    
    // Check if query matches state's aliases
    for (const [stateAbbr, aliases] of Object.entries(stateMap)) {
      if (stateLower === stateAbbr && aliases.some(alias => queryLower.includes(alias))) {
        return true;
      }
    }
    
    // Miami special handling
    if (queryLower.includes('miami') && 
        (cityLower.includes('miami') || cityLower.includes('south beach'))) {
      return true;
    }
    
    return false;
  }

  /**
   * BUILDING TYPE NORMALIZATION AND MATCHING (adapted for DDA)
   */
  static normalizeBuildingType(project) {
    // DDA projects may have different type fields than SPD
    const assetCategory = (project.primary_asset_category || '').toLowerCase().trim();
    const buildingUseRaw = (project.building_use_type || '').toLowerCase().trim();
    const notes = (project.notes || '').toLowerCase().trim();
    
    // Parse building use types - they're semicolon-separated like "Apt; Condo; Ret"
    const buildingUseTypes = buildingUseRaw
      .split(';')
      .map(type => type.trim())
      .filter(type => type.length > 0);
    
    return {
      assetCategory,
      buildingUseTypes,  // Array of individual types
      buildingUseRaw,   // Original concatenated string
      notes,
      combined: `${assetCategory} ${buildingUseRaw} ${notes}`.trim()
    };
  }
  
  static buildingTypeMatches(projectType, queryType) {
    const queryLower = queryType.toLowerCase().trim();
    
    // Mapping of full names to abbreviations used in the database
    const typeAbbreviations = {
      'condominium': ['condo', 'condominium'],
      'apartment': ['apt', 'apartment', 'rental apartment'],
      'retail': ['ret', 'retail'],
      'office': ['offc', 'office'],
      'hotel': ['hotel', 'hospitality'],
      'residential': ['residential'],
      'commercial': ['commercial']
    };
    
    // Get all possible matches for the query (including abbreviations)
    const queryVariants = typeAbbreviations[queryLower] || [queryLower];
    
    // PRIORITY 1: Check if query matches ANY of the individual building use types
    // This ensures "Condominium" ONLY matches projects that have "Condo" as one of their types
    if (projectType.buildingUseTypes && projectType.buildingUseTypes.length > 0) {
      const hasExactMatch = projectType.buildingUseTypes.some(projectUseType => 
        queryVariants.some(variant => {
          // Check for exact match or contains match
          return projectUseType === variant || projectUseType.includes(variant);
        })
      );
      
      if (hasExactMatch) {
        console.log(`✅ MATCH: "${queryType}" found in building use types:`, projectType.buildingUseTypes);
        return true;
      }
    }
    
    // PRIORITY 2: Check asset category (primary_asset_category field)
    if (projectType.assetCategory) {
      const categoryMatch = queryVariants.some(variant => 
        projectType.assetCategory.includes(variant)
      );
      if (categoryMatch) {
        console.log(`✅ MATCH: "${queryType}" found in asset category:`, projectType.assetCategory);
        return true;
      }
    }
    
    // PRIORITY 3: Broad category searches (only if no specific type match found)
    const broadCategories = ['residential', 'commercial', 'industrial', 'mixed-use'];
    if (broadCategories.includes(queryLower)) {
      // For broad categories, check the combined string
      return projectType.combined.includes(queryLower);
    }
    
    console.log(`❌ NO MATCH: "${queryType}" not found in:`, projectType);
    return false;
  }

  /**
   * EXTRACT PROJECT YEAR from various date fields (adapted for DDA)
   */
  static extractProjectYear(project) {
    // Try different date fields in order of preference for DDA
    const dateFields = [
      project.actual_start_date,
      project.planned_start_date, 
      project.actual_end_date,
      project.planned_end_date,
      project.created_at
    ];
    
    for (const dateField of dateFields) {
      if (dateField) {
        const date = new Date(dateField);
        if (!isNaN(date.getTime())) {
          return date.getFullYear();
        }
      }
    }
    
    return null;
  }

  /**
   * Get summary statistics for AI context
   */
  static async getSummaryStats() {
    try {
      const [projects, contractors, drawDetails] = await Promise.all([
        this.getProjects(),
        this.getContractors(), 
        this.getDrawDetails()
      ]);

      const activeProjects = projects.filter(p => p.status === 'A' || p.status === 'active').length;
      const totalBudget = projects.reduce((sum, p) => sum + (p.total_original_budget || 0), 0);
      
      return {
        totalProjects: projects.length,
        activeProjects,
        totalContractors: contractors.length,
        totalBudgetItems: drawDetails.length,
        totalBudget,
        dataSource: 'Live DDA System',
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.warn('DDA summary unavailable:', error.message);
      return {
        totalProjects: 0,
        activeProjects: 0,
        totalContractors: 0,
        totalBudgetItems: 0,
        totalBudget: 0,
        dataSource: 'DDA System (Unavailable)',
        lastUpdated: new Date().toISOString()
      };
    }
  }

  /**
   * Analyze contractor performance
   */
  static async getContractorPerformance(contractorId = null) {
    try {
      const drawDetails = await this.getDrawDetails();
      
      if (contractorId) {
        return drawDetails.filter(detail => detail.contractor_id === contractorId);
      }
      
      // Group by contractor for performance analysis
      const performanceMap = {};
      drawDetails.forEach(detail => {
        if (detail.company) {
          if (!performanceMap[detail.company]) {
            performanceMap[detail.company] = {
              company: detail.company,
              totalProjects: 0,
              totalBudget: 0,
              totalBilled: 0,
              onTimePerformance: 0,
              budgetVariance: 0
            };
          }
          
          performanceMap[detail.company].totalProjects++;
          performanceMap[detail.company].totalBudget += detail.current_budget || 0;
          performanceMap[detail.company].totalBilled += detail.billed_to_date || 0;
        }
      });
      
      return Object.values(performanceMap);
    } catch (error) {
      console.warn('DDA contractor performance unavailable:', error.message);
      return [];
    }
  }

  /**
   * Get cost analysis by building type, location, etc.
   */
  static async getCostAnalysis(filters = {}) {
    try {
      const projects = await this.getProjects();
      const drawDetails = await this.getDrawDetails();
      
      let filteredProjects = projects;
      
      // Apply filters dynamically
      if (filters.location) {
        const location = filters.location.toLowerCase();
        filteredProjects = filteredProjects.filter(p => 
          (p.city && p.city.toLowerCase().includes(location)) ||
          (p.state && p.state.toLowerCase().includes(location)) ||
          (p.address_line_1 && p.address_line_1.toLowerCase().includes(location))
        );
      }
      
      if (filters.minCostPerSF) {
        filteredProjects = filteredProjects.filter(p => {
          const costPerSF = p.total_original_budget / p.total_gsf;
          return costPerSF >= filters.minCostPerSF;
        });
      }
      
      if (filters.maxCostPerSF) {
        filteredProjects = filteredProjects.filter(p => {
          const costPerSF = p.total_original_budget / p.total_gsf;
          return costPerSF <= filters.maxCostPerSF;
        });
      }
      
      return {
        projects: filteredProjects,
        totalProjects: filteredProjects.length,
        averageCostPerSF: filteredProjects.length > 0 
          ? filteredProjects.reduce((sum, p) => sum + (p.total_original_budget / p.total_gsf || 0), 0) / filteredProjects.length
          : 0,
        totalValue: filteredProjects.reduce((sum, p) => sum + (p.total_original_budget || 0), 0)
      };
    } catch (error) {
      console.warn('DDA cost analysis unavailable:', error.message);
      return { projects: [], totalProjects: 0, averageCostPerSF: 0, totalValue: 0 };
    }
  }
}

// REMOVED: All hardcoded temporal parsing functions
// AI now handles all temporal intelligence dynamically and contextually

/**
 * AI-Driven Analysis Helpers
 */
DDADataService.getDataInsights = function(data) {
  return {
    dataFreshness: new Date().toISOString(),
    processingApproach: 'ai-driven-dynamic',
    enhancedForAI: true,
    capabilities: [
      'Dynamic temporal analysis',
      'Contextual geographic understanding', 
      'Adaptive company matching',
      'Intelligent cost analysis',
      'Cross-parameter relationship detection'
    ]
  };
};

export default DDADataService;
